import { ROLE_BROWSER_MIRADOR } from '@aipb/gulliver/browser-mirador/auth';
import { ROLE_BROWSER } from '@aipb/gulliver/browser/auth';
import {
  ROLE_CATALOG,
  ROLE_CATALOG_CAN_CROP,
  ROLE_CATALOG_DOWNLOAD,
  ROLE_CATALOG_DOWNLOAD_BATCH,
  ROLE_CATALOG_DOWNLOAD_INTERNAL
} from '@aipb/gulliver/catalog/auth';
import {
  CATALOG_CASTOR_CONFIG_LOCAL_STORAGE_KEY,
  CATALOG_DETAIL_LOCAL_STORAGE_KEY,
  CATALOG_FACET_LOCAL_STORAGE_KEY,
  CATALOG_SEARCH_LOCAL_STORAGE_KEY
} from '@aipb/gulliver/catalog/model';
import { ROLE_CONFIG_VIEWER } from '@aipb/gulliver/config-viewer/auth';
import { DROPBOX_WRAPPER_API_URL } from '@aipb/gulliver/dropbox/model';
import { ROLE_DYNAMIC_GALLERY } from '@aipb/gulliver/dynamic-gallery/auth';
import { DYNAMIC_GALLERY_LOCAL_STORAGE_KEY } from '@aipb/gulliver/dynamic-gallery/model';
import { APP_CONFIG, APP_NAME, IS_PRODUCTION } from '@aipb/gulliver/env/util';
import { ROLE_EXPLORER } from '@aipb/gulliver/explorer/auth';
import { EXPLORER_EXPLORATION_LOCAL_STORAGE_KEY } from '@aipb/gulliver/explorer/model';
import { FEATURE_TOGGLE_FEATURES } from '@aipb/gulliver/feature-toggle';
import { FRAME_LOCAL_STORAGE_KEY } from '@aipb/gulliver/frame/model';
import { ROLE_FULLTEXT } from '@aipb/gulliver/fulltext/auth';
import { ROLE_KEYCLOAK_MANAGER } from '@aipb/gulliver/keycloak-manager/auth';
import { ROLE_LANA } from '@aipb/gulliver/lana/auth';
import { LANA_SETTINGS_LOCAL_STORAGE_KEY } from '@aipb/gulliver/lana/model';
import { ROLE_MERC } from '@aipb/gulliver/merc/auth';
import { MERC_FAVORITE_LOCAL_STORAGE_KEY, MERC_USER_FACET_LOCAL_STORAGE_KEY } from '@aipb/gulliver/merc/model';
import { ROLE_PERSONAL_CONTENT, ROLE_PERSONAL_CONTENT_COLLECTIONS } from '@aipb/gulliver/personal-content/auth';
import { PERSONAL_CONTENT_CONFIG_LOCAL_STORAGE_KEY } from '@aipb/gulliver/personal-content/model';
import { ROLE_REPORT_TESTER } from '@aipb/gulliver/report-tester/auth';
import { RESOLVER_API_URL } from '@aipb/gulliver/resolver';
import { TRANSLATION } from '@aipb/gulliver/translation';
import {
  APP_REQUIRED_ROLES,
  AppRequiredRoles,
  AuthService,
  GROUP_DESCRIPTIONS,
  GroupDescriptions,
  ROLE_DESCRIPTIONS,
  RoleDescriptions
} from '@aipb/shared/auth';
import { cookiesAvailable, getAppVersion, localeStorageAvailable } from '@aipb/shared/util/other';
import { FullscreenOverlayContainer, OverlayContainer } from '@angular/cdk/overlay';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeCs from '@angular/common/locales/cs';
import localeEn from '@angular/common/locales/en';
import localeDe from '@angular/common/locales/de';
import localeRu from '@angular/common/locales/ru';
import { APP_INITIALIZER, enableProdMode, ErrorHandler, importProvidersFrom, isDevMode } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDateFnsModule } from '@angular/material-date-fns-adapter';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { bootstrapApplication, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, Router, RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { enableElfProdMode } from '@ngneat/elf';
import { devTools } from '@ngneat/elf-devtools';
import { ElfNgRouterStoreModule } from '@ngneat/elf-ng-router-store';
import { captureException, createErrorHandler, init } from '@sentry/angular';
import { Buffer } from 'buffer';
import { cs } from 'date-fns/locale';
import EventEmitter from 'events';
import { KeycloakAngularModule } from 'keycloak-angular';
import { isEqual } from 'lodash-es';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { MarkdownModule } from 'ngx-markdown';
import { NgxMasonryModule } from 'ngx-masonry';
import { QuillModule } from 'ngx-quill';
import { SchemaFormModule } from 'ngx-schema-form';
import { forkJoin, mergeMap, tap } from 'rxjs';
import 'zone.js';
import { AppComponent } from './app/app.component';
import { generateRoutes } from './app/app.routes';
import { CastorInterceptor } from './castor.interceptor';
import { ConsoleErrorHandler } from './console-error.handler';
import process from 'process';
import { ROLE_CUSTOMER_CONFIG_EDITOR } from '@aipb/gulliver/customer-config-editor/auth';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_CARD_CONFIG } from '@angular/material/card';
import { TranslocoImportLoader } from './transloco-import-loader';
import { ROLE_ZIP_MANAGER } from '@aipb/gulliver/zip-manager/auth';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { AppConfigModel, AppConfigService } from './app/app.config.service';
import { AppIdentifier } from '@aipb/gulliver/env/model';

function initializeApp(
  appConfigService: AppConfigService,
  auth: AuthService,
  transloco: TranslocoService,
  router: Router
) {
  return () => {
    return appConfigService.loadConfig().pipe(
      tap(() => {
        if (appConfigService.production) {
          enableProdMode();
          enableElfProdMode();

          if (window) {
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            window.console.log = function () {};
          }
        } else {
          devTools();
        }

        const APP_VERSION_KEY = appConfigService.project + '-app-version';
        const APP_CONFIG_KEY = appConfigService.project + '-app-config';
        const APP_GIT_REVISION = appConfigService.project + '-app-git-revision';

        const localeStorageKeys: string[] = [
          CATALOG_SEARCH_LOCAL_STORAGE_KEY,
          CATALOG_CASTOR_CONFIG_LOCAL_STORAGE_KEY,
          CATALOG_DETAIL_LOCAL_STORAGE_KEY,
          CATALOG_FACET_LOCAL_STORAGE_KEY,
          DYNAMIC_GALLERY_LOCAL_STORAGE_KEY,
          EXPLORER_EXPLORATION_LOCAL_STORAGE_KEY,
          FRAME_LOCAL_STORAGE_KEY,
          LANA_SETTINGS_LOCAL_STORAGE_KEY,
          MERC_FAVORITE_LOCAL_STORAGE_KEY,
          MERC_USER_FACET_LOCAL_STORAGE_KEY,
          PERSONAL_CONTENT_CONFIG_LOCAL_STORAGE_KEY
        ];

        if (localeStorageAvailable()) {
          const appVersion = localStorage.getItem(APP_VERSION_KEY);
          const revision = localStorage.getItem(APP_GIT_REVISION);

          if (appVersion !== getAppVersion(appConfigService.project)) {
            localeStorageKeys.forEach((key) => {
              localStorage.removeItem(`${appConfigService.project}-${key}`);
            });

            localStorage.removeItem('ngFormsManager');
            localStorage.setItem(APP_VERSION_KEY, getAppVersion(appConfigService.project));
          }
        } else {
          captureException('[User has disabled localStorage]');
        }

        if (localeStorageAvailable()) {
          const configLocalStorage = localStorage.getItem(APP_CONFIG_KEY);
          if (!isEqual(JSON.parse(configLocalStorage), appConfigService.config)) {
            localeStorageKeys.forEach((key) => {
              localStorage.removeItem(`${appConfigService.project}-${key}`);
            });
            localStorage.removeItem('ngFormsManager');
            localStorage.setItem(APP_CONFIG_KEY, JSON.stringify(appConfigService.config));
          }
        }

        const packageVersion = require('../../../package.json').version;
        let release = packageVersion;

        if (appConfigService.sentryVersionPackageName) {
          release = `${appConfigService.sentryVersionPackageName}@${packageVersion}`;
        }

        transloco.setDefaultLang(appConfigService.config.frame.defaultLanguage);
        transloco.setActiveLang(appConfigService.config.frame.defaultLanguage);

        router.resetConfig(generateRoutes(<AppIdentifier[]>Object.keys(appConfigService.config.applications)));

        init({
          dsn: appConfigService.sentryDsn,
          release: release,
          environment: `${appConfigService.project}_${appConfigService.environment}`,
          tracesSampleRate: 0.2,
          ignoreErrors: ['Non-Error exception captured']
        });
      }),
      mergeMap((config: AppConfigModel) => auth.init(config.auth)),
      mergeMap(() => forkJoin(appConfigService.config.frame.languages.map((lang) => transloco.load(lang.name))))
    );
  };
}

// sax.js needs these
window.Buffer = Buffer;
window['EventEmitter'] = EventEmitter;
window.process = process;

registerLocaleData(localeCs);
registerLocaleData(localeEn);
registerLocaleData(localeDe);
registerLocaleData(localeRu);

const setupErrorHandler = (appConfigService: AppConfigService) => {
  if (!appConfigService.production) {
    return new ConsoleErrorHandler();
  }

  return createErrorHandler({
    showDialog: false
  });
};

if (!cookiesAvailable()) {
  alert(
    'Zdá se, že bezpečnostní nastavení Vašeho prohlížeče nám neumožňuje ukládání cookies nutných pro správný chod této aplikace. Prosím udělte aplikaci výjimku pro ukládání cookies a načtěte stránku znovu. (Případně požádejte o asistenci svého správce IT.)'
  );
  captureException('[User has disabled cookies]');
} else {
  bootstrapApplication(AppComponent, {
    providers: [
      {
        provide: APP_INITIALIZER,
        multi: true,
        useFactory: initializeApp,
        deps: [AppConfigService, AuthService, TranslocoService, Router]
      },
      {
        provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
        deps: [AppConfigService],
        useFactory: (appConfig: AppConfigService) => {
          return appConfig.matFormFieldDefaultOptions;
        }
      },
      {
        provide: MAT_CARD_CONFIG,
        deps: [AppConfigService],
        useFactory: (appConfig: AppConfigService) => {
          return appConfig.matCardDefaultOptions;
        }
      },
      provideRouter([]),
      { provide: HTTP_INTERCEPTORS, useClass: CastorInterceptor, multi: true },
      provideHttpClient(withInterceptorsFromDi()),
      provideTransloco({
        config: {
          reRenderOnLangChange: true,
          availableLangs: ['en', 'cs', 'de', 'ru'],
          fallbackLang: 'en',
          prodMode: !isDevMode()
        },
        loader: TranslocoImportLoader
      }),
      provideTranslocoMessageformat({
        locales: ['en', 'cs', 'de', 'ru']
      }),
      importProvidersFrom([
        RouterModule.forRoot([], {
          paramsInheritanceStrategy: 'always',
          anchorScrolling: 'enabled',
          bindToComponentInputs: true,
          preloadingStrategy: PreloadAllModules
        }),
        !isDevMode() ? NgxGoogleAnalyticsModule : [],
        !isDevMode() ? NgxGoogleAnalyticsRouterModule : [],
        BrowserAnimationsModule,
        MatDialogModule,
        MatAutocompleteModule,
        MatSnackBarModule,
        MatSelectModule,
        MatChipsModule,
        MatMenuModule,
        KeycloakAngularModule,
        HammerModule,
        LazyLoadImageModule,
        FormsModule,
        ReactiveFormsModule,
        MarkdownModule.forRoot(),
        NgxMasonryModule,
        FontAwesomeModule,
        QuillModule.forRoot(),
        SchemaFormModule.forRoot(),
        ElfNgRouterStoreModule,
        MatDatepickerModule,
        MatDateFnsModule,
        ClipboardModule
      ]),
      {
        provide: STEPPER_GLOBAL_OPTIONS,
        useValue: { displayDefaultIndicatorType: false }
      },
      { provide: MAT_DATE_LOCALE, useValue: cs },
      { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
      {
        provide: APP_CONFIG,
        deps: [AppConfigService],
        useFactory: (appConfig: AppConfigService) => {
          return appConfig.config;
        }
      },
      {
        provide: IS_PRODUCTION,
        deps: [AppConfigService],
        useFactory: (appConfig: AppConfigService) => {
          return appConfig.production;
        }
      },
      {
        provide: APP_NAME,
        deps: [AppConfigService],
        useFactory: (appConfig: AppConfigService) => {
          return appConfig.project;
        }
      },
      {
        provide: TRANSLATION,
        deps: [AppConfigService],
        useFactory: (appConfig: AppConfigService) => {
          return appConfig.translations;
        }
      },
      {
        provide: APP_REQUIRED_ROLES,
        useValue: <AppRequiredRoles>{
          browser: [ROLE_BROWSER],
          'browser-mirador': [ROLE_BROWSER_MIRADOR],
          catalog: [ROLE_CATALOG],
          'config-viewer': [ROLE_CONFIG_VIEWER],
          'customer-config-editor': [ROLE_CUSTOMER_CONFIG_EDITOR],
          dashboard: ['dashboard'],
          'dynamic-gallery': [ROLE_DYNAMIC_GALLERY],
          explorer: [ROLE_EXPLORER],
          fulltext: [ROLE_FULLTEXT],
          'keycloak-manager': [ROLE_KEYCLOAK_MANAGER],
          lana: [ROLE_LANA],
          merc: [ROLE_MERC],
          'personal-content': [ROLE_PERSONAL_CONTENT, ROLE_PERSONAL_CONTENT_COLLECTIONS],
          'report-tester': [ROLE_REPORT_TESTER],
          'zip-manager': [ROLE_ZIP_MANAGER]
        }
      },
      {
        provide: ROLE_DESCRIPTIONS,
        useValue: <RoleDescriptions>{
          [ROLE_BROWSER]: 'Uživatel má přístup k aplikaci Browser',
          [ROLE_BROWSER_MIRADOR]: 'Uživatel má přístup k aplikaci Browser Mirador',
          [ROLE_CATALOG]: 'Uživatel má přístup k aplikaci Catalog',
          [ROLE_CONFIG_VIEWER]: 'Uživatel má přístup k aplikaci Config Viewer',
          [ROLE_CUSTOMER_CONFIG_EDITOR]: 'Uživatel má přístup k aplikaci Customer Config Editor',
          [ROLE_DYNAMIC_GALLERY]: 'Uživatel má přístup k aplikaci Dynamic Gallery',
          [ROLE_EXPLORER]: 'Uživatel má přístup k aplikaci Explorer',
          [ROLE_FULLTEXT]: 'Uživatel má přístup k aplikaci Fulltext',
          [ROLE_KEYCLOAK_MANAGER]: 'Uživatel má přístup k aplikaci Keycloak Manager',
          [ROLE_LANA]: 'Uživatel má přístup k aplikaci Lana',
          [ROLE_MERC]: 'Uživatel má přístup k aplikaci Merc',
          [ROLE_PERSONAL_CONTENT]:
            'Uživatel může pouze hvězdičkovat, nemá přístup do aplikace Personal Content, v pluginu vidí pouze oblíbené',
          [ROLE_PERSONAL_CONTENT_COLLECTIONS]:
            'Uživatel má přístup k aplikaci Personal Content a zároveň má přístup ke kolekcím',
          [ROLE_REPORT_TESTER]: 'Uživatel má přístup k aplikaci Report Tester',
          [ROLE_CATALOG_CAN_CROP]: '[Catalog] Uživatel může ořezávat obrázek',
          [ROLE_CATALOG_DOWNLOAD]: '[Catalog] Uživatel může stahovat obrázek',
          [ROLE_CATALOG_DOWNLOAD_BATCH]: '[Catalog] Uživatel může stahovat hromadně obrázky',
          [ROLE_CATALOG_DOWNLOAD_INTERNAL]: '[Catalog] Uživatel může stahovat zdrojový obrázek z Dropboxu'
        }
      },
      {
        provide: GROUP_DESCRIPTIONS,
        useValue: <GroupDescriptions>{
          '/member/admin': 'Skupina pro admin uživatele',
          '/member/public': 'Skupina pro běžné uživatele'
        }
      },
      {
        provide: RESOLVER_API_URL,
        deps: [AppConfigService],
        useFactory: (appConfig: AppConfigService) => {
          return appConfig.resolverApiUrl;
        }
      },
      {
        provide: DROPBOX_WRAPPER_API_URL,
        deps: [AppConfigService],
        useFactory: (appConfig: AppConfigService) => {
          return appConfig.dropboxWrapperApiBaseUrl;
        }
      },
      {
        provide: ErrorHandler,
        deps: [AppConfigService],
        useFactory: setupErrorHandler
      },
      {
        provide: FEATURE_TOGGLE_FEATURES,
        deps: [AppConfigService],
        useFactory: (appConfig: AppConfigService) => {
          return appConfig.features;
        }
      }
    ]
  }).catch((err) => {
    // eslint-disable-next-line no-console
    console.error(err);
    captureException(err);
  });
}
